<div class="ai-broker-shell" [class.rtl]="languageService.currentLanguage() === 'ar'">
  <div class="grid h-full m-0 align-items-stretch">
    <!-- Sidebar / Info Panel -->
    @if (!activeCanvasPath()) {
    <div class="col-12 md:col-4 lg:col-3 p-2 sidebar-col">
      <div class="ai-overview-panel">
        <div class="overview-hero">
          <div class="profile-lockup">
            <p-avatar [image]="aiProfile.profilePictureUrl" shape="circle" size="large"></p-avatar>
            <div>
              <span class="eyebrow">AI Operations Desk</span>
              <h1>{{ aiProfile.fullName }}</h1>
              <div class="status-row">
                <span class="status-dot"></span>
                <span>{{ aiProfile.status }}</span>
              </div>
            </div>
          </div>

          <p>
            LoadGate AI is the conversational assistant for the LoadGate logistics marketplace. It turns chats,
            documents, voice notes, and trip updates into structured freight actions.
          </p>
        </div>

        <div class="overview-section">
          <span class="section-label">What It Handles</span>
          <div class="feature-list">
            <article class="feature-item">
              <i class="pi pi-inbox"></i>
              <div>
                <strong>Freight Intake</strong>
                <span>Reads load requests from text, images, documents, and voice notes.</span>
              </div>
            </article>
            <article class="feature-item">
              <i class="pi pi-truck"></i>
              <div>
                <strong>Capacity Matching</strong>
                <span>Finds available trucks, compares fit, and supports rate negotiation.</span>
              </div>
            </article>
            <article class="feature-item">
              <i class="pi pi-id-card"></i>
              <div>
                <strong>Driver Onboarding</strong>
                <span>Captures ID, license, vehicle documents, and operational readiness.</span>
              </div>
            </article>
            <article class="feature-item">
              <i class="pi pi-file-check"></i>
              <div>
                <strong>Shipment Workflow</strong>
                <span>Assists with booking, POD, waybill, invoice, and payment steps.</span>
              </div>
            </article>
          </div>
        </div>

        <div class="overview-section process-section">
          <span class="section-label">Workflow</span>
          <div class="process-rail">
            <div>
              <span>1</span>
              <div>
                <strong>Understand</strong>
                <small>Classify intent and extract lane, equipment, timing, and contact context.</small>
              </div>
            </div>
            <div>
              <span>2</span>
              <div>
                <strong>Structure</strong>
                <small>Create or update marketplace records with missing-field awareness.</small>
              </div>
            </div>
            <div>
              <span>3</span>
              <div>
                <strong>Act</strong>
                <small>Open details, suggest matches, request documents, and continue operations.</small>
              </div>
            </div>
          </div>
        </div>

        <div class="overview-footer">
          <span class="section-label">{{ 'AI_BROKER.CAPABILITIES' | translate }}</span>
          <div class="capability-grid">
            <span>OCR</span>
            <span>Voice</span>
            <span>WhatsApp</span>
            <span>Matching</span>
            <span>Documents</span>
            <span>Payments</span>
          </div>
        </div>
      </div>
    </div>
    }

    <!-- Chat Column -->
    <div class="col-12 p-2 flex flex-column chat-column"
      [class]="activeCanvasPath() ? 'lg:col-5' : 'md:col-8 lg:col-9'">
      <div class="chat-container">
        <!-- Messages Section -->
        <div
          class="messages-section surface-card p-3 border-round-lg border-1 border-gray-300 flex-grow-1 flex flex-column min-height-0">
          <div class="flex flex-column sm:flex-row sm:align-items-center sm:justify-content-between gap-3 mb-4">
            <div class="flex align-items-center gap-2">
              <p-tag [value]="'COMMON.TODAY' | translate" severity="info" rounded="true"></p-tag>
              <span class="font-medium text-color-secondary">{{ timelineLabel }}</span>
            </div>
          </div>

          <div #chatScrollPanel class="messages-container pr-2" (scroll)="onScroll()">
            @if (isLoadingMessages() && messages().length > 0) {
            <div class="flex justify-content-center p-3">
              <i class="pi pi-spin pi-spinner text-primary text-xl"></i>
            </div>
            }
            @for (message of messages(); track message.messageId) {
            <article class="message" [class.from-user]="isUserSender(message)">
              @if (isAiSender(message)) {
              <p-avatar [image]="message.sender.profilePictureUrl" [label]="message?.sender?.fullName?.at(0) || 'A'"
                shape="circle" size="large"></p-avatar>
              }

              <div class="bubble-stack">
                <div class="name-row">{{ message.sender.fullName }}</div>

                @for (content of message.contents; track $index) {
                @if (isText(content)) {
                <div class="bubble" [class.ai-bubble]="isAiSender(message)" [class.user-bubble]="isUserSender(message)">
                  <div [innerHTML]="convertToHtml(content.text)"></div>
                </div>
                }

                @if (isImage(content)) {
                <app-chat-image [data]="content.data"></app-chat-image>
                }

                @if (isAudio(content)) {
                <app-chat-audio [data]="content.data"></app-chat-audio>
                }

                @if (isVideo(content)) {
                <app-chat-video [data]="content.data"></app-chat-video>
                }

                @if (isTable(content)) {
                <app-chat-table [data]="content.data"></app-chat-table>
                }

                @if (isChart(content)) {
                <app-chat-chart [data]="content.data"></app-chat-chart>
                }

                @if (isLocation(content)) {
                <app-chat-location [data]="content.data"></app-chat-location>
                }

                @if (isContact(content)) {
                <app-chat-contact [data]="content.data"></app-chat-contact>
                }

                @if (isAttachment(content)) {
                <div class="bubble p-3" [class.ai-bubble]="isAiSender(message)"
                  [class.user-bubble]="isUserSender(message)">
                  <div class="flex align-items-center gap-3">
                    <i class="pi pi-file-pdf text-2xl"></i>
                    <div class="flex-grow-1">
                      <div class="font-bold text-sm">{{ content.name }}</div>
                      <div class="text-xs opacity-70">{{ content.mimeType }} • {{ content.sizeLabel }}</div>
                    </div>
                    @if (content.url) {
                    <a [href]="content.url" target="_blank" class="p-button-link">
                      <button pButton [label]="'COMMON.OPEN' | translate" icon="pi pi-external-link"
                        class="p-button-sm p-button-outlined p-button-primary"></button>
                    </a>
                    }
                  </div>
                </div>
                }

                @if (isActionButtons(content)) {
                <div class="flex gap-2 flex-wrap mt-2" [class.justify-content-end]="isUserSender(message)">
                  @for (btn of content.data; track $index) {
                  <button pButton type="button" [label]="btn.label" [icon]="btn.icon || ''"
                    (click)="onActionButtonClick(btn)"
                    [class]="'p-button-rounded p-button-sm ' + (btn.style === 'outlined' ? 'p-button-outlined' : btn.style === 'secondary' ? 'p-button-secondary' : btn.style === 'danger' ? 'p-button-danger' : '')">
                  </button>
                  }
                </div>
                }
                }

                <div class="meta-row">
                  <span>{{ formatTime(message.timestamp) }}</span>
                  @if (message.status) {
                  <span class="ml-2 font-bold">{{ message.status }}</span>
                  }
                </div>
              </div>

              @if (isUserSender(message)) {
              <p-avatar [image]="message.sender.profilePictureUrl" [label]="message?.sender?.fullName?.at(0) || 'U'"
                shape="circle" size="large"></p-avatar>
              }
            </article>
            }

            @if (isAiTyping()) {
            <article class="message">
              <p-avatar [image]="aiProfile.profilePictureUrl" shape="circle" size="large"></p-avatar>
              <div class="bubble-stack">
                <div class="bubble ai-bubble flex gap-1 align-items-center py-2 px-3">
                  <span class="p-1 border-circle surface-400"></span>
                  <span class="p-1 border-circle surface-400"></span>
                  <span class="p-1 border-circle surface-400"></span>
                </div>
              </div>
            </article>
            }
          </div>
        </div>

        <!-- Composer Section -->
        <div class="composer-section surface-card p-3 border-round-lg border-1 border-gray-300">
          <div class="flex align-items-center justify-content-between mb-2">
            <div class="flex align-items-center gap-2 font-bold text-sm">
              <i class="pi pi-microchip-ai"></i>
              <span>{{ 'AI_BROKER.COMPOSER_TITLE' | translate }}</span>
            </div>
            <span class="text-xs text-color-secondary">{{ 'AI_BROKER.COMPOSER_DESC' | translate }}</span>
          </div>

          @if (pendingFile || pendingAudioUrl || isRecording) {
          <div class="flex flex-wrap gap-2 mb-3">
            @if (pendingFile) {
            <div class="flex align-items-center gap-2 p-2 surface-100 border-round">
              @if (isPendingFileImage()) {
              <img [src]="pendingFilePreview" class="h-2rem w-2rem border-round object-cover">
              } @else {
              <i class="pi pi-file text-xl"></i>
              }
              <span class="text-xs truncate max-w-10rem">{{ pendingFileName }}</span>
              <button pButton icon="pi pi-times" class="p-button-text p-button-sm p-button-secondary"
                (click)="removePendingFile()"></button>
            </div>
            }
            @if (pendingAudioUrl) {
            <div class="flex align-items-center gap-2 p-2 bg-green-50 text-green-700 border-round">
              <i class="pi pi-microphone"></i>
              <span class="text-xs">Voice - {{ pendingAudioDuration }}</span>
              <button pButton icon="pi pi-times" class="p-button-text p-button-sm p-button-success"
                (click)="removePendingAudio()"></button>
            </div>
            }
            @if (isRecording) {
            <div class="flex align-items-center gap-2 p-2 bg-red-50 text-red-700 border-round">
              <span class="w-1rem h-1rem border-circle bg-red-500 animate-pulse"></span>
              <span class="text-xs font-bold">{{ 'COMMON.RECORDING' | translate }}</span>
              <button pButton [label]="'COMMON.STOP' | translate" class="p-button-text p-button-sm p-button-danger"
                (click)="stopRecording()"></button>
            </div>
            }
          </div>
          }

          @if (uploadProgress() !== null) {
          <div class="mb-3 px-1">
            <p-progressBar [value]="uploadProgress()!" [showValue]="false" [style]="{ height: '6px' }"></p-progressBar>
            <div class="text-xs text-color-secondary mt-1 flex justify-content-between font-medium">
              <span>{{ 'COMMON.UPLOADING' | translate }}</span>
              <span>{{ uploadProgress() }}%</span>
            </div>
          </div>
          }

          <div class="composer-body">
            <div class="flex gap-1">
              <button pButton icon="pi pi-paperclip" class="p-button-text p-button-secondary p-button-rounded"
                (click)="fileInput.click()"></button>
              <button pButton [icon]="isRecording ? 'pi pi-stop-circle' : 'pi pi-microphone'"
                [class.p-button-danger]="isRecording" class="p-button-text p-button-secondary p-button-rounded"
                (click)="toggleRecording()"></button>
            </div>

            <textarea pInputTextarea rows="1" [(ngModel)]="composerText"
              [placeholder]="'AI_BROKER.COMPOSER_PLACEHOLDER' | translate" (keydown.enter)="onComposerEnter($event)"
              class="w-full"></textarea>

            <button pButton icon="pi pi-send" class="p-button-rounded send-button" [disabled]="!canSend()"
              (click)="sendMessage()"></button>
          </div>

          <input #fileInput type="file" accept="*/*" (change)="onFileSelected($event)" class="hidden-file">
          @if (recordingError) {
          <div class="text-red-500 text-xs mt-2 px-1">{{ recordingError }}</div>
          }
        </div>
      </div>
    </div>
    <!-- Canvas Column -->
    @if (activeCanvasPath()) {
    <div class="col-12 lg:col-7 p-2 flex flex-column h-full">
      <div class="surface-card border-round-lg border-1 border-gray-300 h-full flex flex-column overflow-hidden">
        <!-- Canvas Header -->
        <div class="flex align-items-center justify-content-between p-3 border-bottom-1 border-gray-300 bg-gray-50">
          <div class="font-bold text-700 flex align-items-center gap-2">
            <i class="pi pi-microchip-ai"></i>
            <span>{{ canvasTitle() }}</span>
          </div>
          <div class="flex gap-1">
            <button pButton icon="pi pi-refresh" class="p-button-rounded p-button-text p-button-secondary p-button-sm"
              (click)="reloadCanvas()" [pTooltip]="'AI_BROKER.RELOAD_CANVAS' | translate"
              tooltipPosition="left"></button>
            <button pButton icon="pi pi-times" class="p-button-rounded p-button-text p-button-secondary p-button-sm"
              (click)="closeCanvas()" [pTooltip]="'AI_BROKER.CLOSE_DETAILS' | translate"
              tooltipPosition="left"></button>
          </div>
        </div>

        <!-- Router Outlet for Canvas -->
        <div class="canvas-body relative">
          <router-outlet name="canvas"></router-outlet>
        </div>
      </div>
    </div>
    }
  </div>
</div>
